import * as React from 'react';
import { RadioGroup } from '@headlessui/react';
import { RadioBullet } from './RadioBullet';

interface ContainerProps<T = string> {
  value: T;
  onChange: (value: T) => void;
}

function Container<T>({
  children,
  value,
  onChange,
}: React.PropsWithChildren<ContainerProps<T>>) {
  return (
    <RadioGroup className="flex w-full gap-2" value={value} onChange={onChange}>
      {children}
    </RadioGroup>
  );
}

type RadioChoiceProps = Parameters<typeof RadioGroup.Option>[0];

// type HHH = GGG['']

export interface ChoiceProps<T = string>
  extends Omit<RadioChoiceProps, 'value'> {
  value: T;
  checkedClass?: string;
  compact?: boolean;
  children: React.ReactNode;
}

function Option<T>({
  value,
  children,
  checkedClass = 'bg-primary-100 border-primary-600',
  compact = false,
}: React.PropsWithChildren<ChoiceProps<T>>) {
  return (
    <RadioGroup.Option
      value={value}
      className={({ checked, active }) =>
        `flex-1 rounded-md 
        overflow-hidden transition-colors duration-300 focus:outline-none
        shadow-sm
        text-center
        text-xs sm:text-sm
        border
         ${
           checked
             ? checkedClass
             : 'border-grey-200 hover:border-grey-600 bg-white'
         } 
         ${active ? 'ring-2 ring-primary-300 ring-opacity-60 z-10' : ''}`
      }
    >
      {({ checked }) => (
        <RadioGroup.Label
          className={`h-full flex justify-between items-center p-3 sm:p-4 cursor-pointer break-all ${
            compact ? 'text-center md:text-left' : ''
          }`}
        >
          <div className="flex-grow break-normal">{children}</div>
          <RadioBullet
            checked={checked}
            className={compact ? 'hidden md:block' : undefined}
          />
        </RadioGroup.Label>
      )}
    </RadioGroup.Option>
  );
}

interface Props<T extends string | number> {
  options: ChoiceProps<T>[];
  value: T;
  onChange: (value: T) => void;
}

function RadioChoice<T extends string | number>({
  options,
  value,
  onChange,
}: Props<T>) {
  const compact = options.length > 2;
  return (
    <Container value={value} onChange={onChange}>
      {options.map((props) => (
        <Option {...props} key={props.value} compact={compact}></Option>
      ))}
    </Container>
  );
}

RadioChoice.Option = Option;
RadioChoice.Container = Container;

export { RadioChoice };
